export function slugify(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getCategorySlug(category) {
  if (!category) return '';
  if (category.effective_slug) return category.effective_slug;
  const stored = typeof category.slug === 'string' ? category.slug.trim() : '';
  if (stored) return stored;
  return slugify(category.name || '');
}

export function getSubcategorySlug(subcategory) {
  if (!subcategory) return '';
  if (subcategory.effective_slug) return subcategory.effective_slug;
  const stored = typeof subcategory.slug === 'string' ? subcategory.slug.trim() : '';
  if (stored) return stored;
  return slugify(subcategory.name || '');
}

export function findCategoryBySlug(categories, slug) {
  if (!slug) return undefined;
  const normalized = slugify(slug);
  return categories.find((category) => getCategorySlug(category) === normalized);
}

export function findCategoryBySlugOrName(categories, value) {
  if (!value) return undefined;
  const bySlug = findCategoryBySlug(categories, value);
  if (bySlug) return bySlug;
  const normalized = slugify(value);
  return categories.find(
    (category) =>
      slugify(category.name || '') === normalized ||
      (category.name || '').toLowerCase() === value.toLowerCase().trim()
  );
}

export function findSubcategoryBySlug(subcategories, slug, categorySlug = null, categories = []) {
  if (!slug) return undefined;
  const normalized = slugify(slug);
  const parentCategory = categorySlug ? findCategoryBySlug(categories, categorySlug) : null;
  return subcategories.find((subcategory) => {
    if (parentCategory && subcategory.category_id !== parentCategory.id) return false;
    return getSubcategorySlug(subcategory) === normalized;
  });
}

export function findSubcategoryBySlugOrName(subcategories, value, categorySlug = null, categories = []) {
  if (!value) return undefined;
  const bySlug = findSubcategoryBySlug(subcategories, value, categorySlug, categories);
  if (bySlug) return bySlug;
  const normalized = slugify(value);
  const parentCategory = categorySlug ? findCategoryBySlug(categories, categorySlug) : null;
  return subcategories.find((subcategory) => {
    if (parentCategory && subcategory.category_id !== parentCategory.id) return false;
    return (
      slugify(subcategory.name || '') === normalized ||
      (subcategory.name || '').toLowerCase() === value.toLowerCase().trim()
    );
  });
}

export function buildBrowseSearchParams({ category = '', subcategory = '', persona = '', search = '' } = {}) {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (subcategory) params.set('subcategory', subcategory);
  if (persona) params.set('persona', persona);
  if (search) params.set('search', search);
  return params;
}

export function browseSearchesEqual(searchA, searchB) {
  const paramsA = buildBrowseSearchParamsFromString(searchA);
  const paramsB = buildBrowseSearchParamsFromString(searchB);
  const keys = ['category', 'subcategory', 'persona', 'search'];
  return keys.every((key) => (paramsA.get(key) || '') === (paramsB.get(key) || ''));
}

export function buildBrowseSearchParamsFromString(search = '') {
  const normalized = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(normalized);
}

export function resolveCategorySlugFromParams(params, categories) {
  const legacyId = params.get('category_id');
  if (legacyId) {
    const category = categories.find((item) => item.id === legacyId);
    if (category) return getCategorySlug(category);
  }

  const raw = params.get('category');
  if (!raw) return '';

  const bySlug = findCategoryBySlug(categories, raw);
  if (bySlug) return getCategorySlug(bySlug);

  const byName = findCategoryBySlugOrName(categories, raw);
  return byName ? getCategorySlug(byName) : slugify(raw);
}

export function resolveSubcategorySlugFromParams(params, subcategories, categories, categorySlug) {
  const legacyId = params.get('subcategory_id');
  if (legacyId) {
    const subcategory = subcategories.find((item) => item.id === legacyId);
    if (subcategory) return getSubcategorySlug(subcategory);
  }

  const raw = params.get('subcategory');
  if (!raw) return '';

  const bySlug = findSubcategoryBySlug(subcategories, raw, categorySlug, categories);
  if (bySlug) return getSubcategorySlug(bySlug);

  const byName = findSubcategoryBySlugOrName(subcategories, raw, categorySlug, categories);
  return byName ? getSubcategorySlug(byName) : slugify(raw);
}
